<template>
  <UikitSwiper
    class="osk-stories-main-slider"
    :breakpoints="sliderBreakpoints"
    :long-swipes-ratio="isDesktop ? 1 : 0.1"
    @swiper="setMainSlider"
    @slide-change="onMainSlideChange"
    @touch-start="handleTouchStart"
    @touch-end="handleTouchEnd"
  >
    <UikitSwiperSlide
      v-for="(num, i) in slidesLength"
      :key="i"
      class="osk-stories-main-slider__item"
      @click="slideTo(i, speed)"
    >
      <BusinessStoriesGroupSlider
        v-if="stories"
        :main-story="getStory(i)"
        :analytics="analytics"
        @swiper="setGroupSlider"
        @slide-change="onSlideChange"
        @mouseenter="handleMouseEnter(i)"
        @mouseleave="handleMouseLeave(i)"
        @slide-prev="handleSlidePrev"
        @slide-next="handleSlideNext"
      />
    </UikitSwiperSlide>
  </UikitSwiper>
</template>

<script lang="ts" setup>
import { SwiperOptions, Swiper as SwiperClass } from 'swiper/types';
import { useApi } from '~/restAPI';
import { Stories } from '~/restAPI/Stories';

import { AnalyticsEvents } from '~/types/analytics';

import {
  StoryDTO, ContentType,
} from '~/restAPI/data-contracts';

import { isDefined } from '~/utils/extra';

interface ISwiperOptions extends SwiperOptions {
  off?: boolean
}

interface IBreakpoints {
  [width: number]: ISwiperOptions;
  [ratio: string]: ISwiperOptions;
}

interface IAnalytics {
  chapter?: ContentType;
  chapter_index?: number;
  segment_id?: number;
  id_chapter?: string;
  item?: string;
}

const sliderBreakpoints: IBreakpoints = {
  0: {
    slidesPerView: 1,
    centeredSlides: true,
    spaceBetween: 16,
  },
  478: {
    slidesPerView: 'auto',
    centeredSlides: true,
  },
  580: {
    slidesPerView: 'auto',
    centeredSlides: true,
  },
  700: {
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 24,
  },
  1024: {
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 40,
  },
};

const props = withDefaults(defineProps<{
  storyIndex: number | null
  isStoriesActive: boolean
  stories?: StoryDTO[]
  usersIds?: string[]
  analytics?: IAnalytics
}>(), {
  stories: undefined,
  usersIds: undefined,
  analytics: undefined,
});

const emits = defineEmits<{
  (e: 'update:storyIndex', value: number | null): void,
  (e: 'update:isStoriesActive', value: boolean): void,
  (e: 'mainSlideChange', swiper: SwiperClass): void,
  (e: 'swiper', swiper: SwiperClass): void,
}>();

const { createApiInstance } = useApi();
const storiesApi = createApiInstance(Stories);

const autoplayDelay = ref(5000);
const speed = ref(500);
const mainSlider = ref<SwiperClass | null>(null);
const groupSliders = ref<SwiperClass[]>([]);

const transform = ref(-100);
const currentSpan = ref<HTMLElement | null>(null);

const { $addEvent } = useNuxtApp();
const { isDesktop } = useUiBreakpoints();

const slidesLength = computed(() => {
  const length = (props.stories?.length ?? 0) * 2;

  return length > 50 ? 50 : length;
});
const activeIndex = computed(() => mainSlider.value?.activeIndex);

const activeGroupSlider = computed(() => (isDefined(activeIndex.value) ? groupSliders.value[activeIndex.value] : undefined));

function getStory(i: number) {
  const { stories } = props;

  if (!stories) return undefined;

  const storyIndex = i % stories.length;

  return stories[storyIndex];
}

function setActiveIndex() {
  emits('update:storyIndex', activeIndex.value ?? null);
}

async function setMainSlider(swiper: SwiperClass) {
  mainSlider.value = swiper;
  setProgress();
  slideTo(props.storyIndex);
  emits('swiper', swiper);
}

function setGroupSlider(swiper: SwiperClass) {
  groupSliders.value.push(swiper);
}

function closeStory() {
  stopProgress();
  emits('update:isStoriesActive', false);
  emits('update:storyIndex', null);
}

function slideTo(i: number | null, s = 0) {
  if (isDefined(i)) {
    mainSlider.value?.slideTo(i, s);
  }
}

const { pause, resume } = useIntervalFn(() => {
  if (!currentSpan.value) return;

  transform.value += 1;
  currentSpan.value.style.transform = `translateX(${transform.value}%)`;

  if (activeGroupSlider.value?.isEnd && transform.value === 0) {
    stopProgress();

    if (mainSlider.value) {
      if (mainSlider.value.isEnd) {
        closeStory();
      }

      mainSlider.value.slideNext(speed.value);
    }
  } else if (transform.value === 0) {
    activeGroupSlider.value?.slideNext(speed.value);
  }
}, autoplayDelay.value / 100, { immediate: false });

function handleTouchStart() {
  if (isDesktop.value) return;

  pause();
}

function handleTouchEnd() {
  if (isDesktop.value) return;

  resume();
}

function setProgress() {
  const bullets = activeGroupSlider.value?.el?.querySelectorAll('.swiper-pagination-bullet');

  bullets?.forEach((bullet, index) => {
    const span: HTMLElement | null = bullet.querySelector('.osk-stories-group-slider__pagination-span');

    if (!span) return;

    span.style.transform = 'translateX(-100%)';

    if (index === activeGroupSlider.value?.activeIndex) {
      currentSpan.value = span;
      transform.value = -100;

      resume();
    } else if (index < (activeGroupSlider.value?.activeIndex ?? -1)) {
      span.style.transform = 'translateX(0%)';
    }
  });
}

function stopProgress() {
  pause();
  transform.value = -100;
}

async function updateUserViewSlideStateOnSeenUsingPost(storyId: string, slideId: string) {
  await useAsyncData(() => storiesApi.updateUserViewSlideStateOnSeenUsingPost(storyId, slideId));
}

const sendedIdList: string[] = [];

function onSlideChange(swiper: SwiperClass, isMainSlideChange?: boolean) {
  let mainIndex: number | undefined = 0;
  let groupIndex: number | undefined = 0;
  let slideList = [];

  if (!isMainSlideChange) {
    mainIndex = activeIndex.value;
    groupIndex = swiper.activeIndex - 1;
  } else {
    mainIndex = (activeIndex.value ?? 0) - 1;
    slideList = props.stories![mainIndex]?.slideList ?? [];
    groupIndex = (slideList.filter((s) => !!s.contentURL).length ?? 0) - 1;
  }

  if (props.stories && isDefined(mainIndex) && groupIndex >= 0) {
    slideList = props.stories[mainIndex]?.slideList;
    const slide = slideList?.filter((s) => !!s.contentURL)[groupIndex];

    if (slide && !sendedIdList.includes(slide.id)) {
      sendedIdList.push(slide.id);

      if (props.stories[mainIndex].id) {
        updateUserViewSlideStateOnSeenUsingPost(props.stories[mainIndex].id!, slide.id);
      }

      $addEvent(AnalyticsEvents.BlockView, {
        ...props.analytics,
        page: 'stories',
        id_item: slide.id,
      });
    }
  }

  if (mainSlider.value) {
    stopProgress();
    setProgress();
  }
}

function onMainSlideChange(swiper: SwiperClass) {
  onSlideChange(swiper, true);
  setActiveIndex();
  emits('mainSlideChange', swiper);
}

function handleMouseEnter(index: number) {
  if (activeIndex.value !== index) return;

  pause();
}

function handleMouseLeave(index: number) {
  if (activeIndex.value !== index) return;

  resume();
}

function handleSlidePrev() {
  setTimeout(() => {
    mainSlider.value?.slidePrev();
  }, 0);
}

function handleSlideNext() {
  setTimeout(() => {
    mainSlider.value?.slideNext();
  }, 0);
}

defineExpose({
  closeStory,
});
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-stories-main-slider {
  overflow: visible;
  width: 100%;
  height: 100%;

  @include media-query(md-and-up) {
    height: auto;
  }

  & > .osk-swiper__block {
    display: flex;
    height: 100%;

    & > .swiper-wrapper {
      height: 100% !important;
      align-items: center;

      @include media-query(md-and-up) {
        margin-left: -120px;
      }
    }
  }

  &__item {
    width: 100% !important;
    height: 100% !important;
    transition: height 0.5s ease, width 0.5s ease !important;
    position: relative;
    display: flex;
    align-items: center;

    @include media-query(md-and-up) {
      height: 320px !important;
      width: 181px !important;
      border-radius: 4px;
      overflow: hidden;
    }

    .osk-stories-group-slider__tag,
    .osk-stories-group-slider__sticker,
    .osk-stories-group-slider__slide-user {
      opacity: 0;
      transition: all 0.5s ease;
    }

    .osk-stories-group-slider__title {
      opacity: 1;
      transition: all 0.5s ease;
    }

    &.swiper-slide-prev,
    &.swiper-slide-next {
      cursor: pointer;
    }

    &.swiper-slide-active {
      width: 100%;
      height: 100%;

      @include media-query(md-and-up) {
        width: 436px !important;
        height: 777px !important;
      }

      .osk-stories-group-slider__tag,
      .osk-stories-group-slider__sticker,
      .osk-stories-group-slider__slide-user {
        opacity: 1;
      }

      .osk-stories-group-slider__title {
        opacity: 0;
      }

      .osk-stories-group-slider__pagination {
        top: 9px;
        opacity: 1;
        height: 2px;

        @include media-query(md-and-up) {
          top: 16px;
        }
      }

      .stories-main-slider__item-btn {
        opacity: 1;
        visibility: visible;
      }
    }
  }
}
</style>
